import { hasProductPackages } from '@/lib/package-utils'
import React, { useState } from 'react'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import { format } from 'date-fns'
import dayjs from 'dayjs'
import AdmobReportFilters from './components/AdmobReportFilters'
import useAdmobStore, { useAdmobDatePickerStore } from '@/stores/admob-store'
import AdmobDataTable from './components/AdmobDataTable'
import AdmobChart from './components/AdmobChart'
import RcAdmobManualCheckDialog from './components/RcAdmobManualCheckDialog'
import { fetcher, blobFetcher, postFetcher } from '@/hooks'
import { toast } from 'sonner'
import { useMemo } from 'react'
import Skeleton from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useEffect } from 'react'
import { useContrastModeStore } from '@/components/roi/ContrastModeSelector'
import { getContrastDateRange, getContrastDate } from '@/lib/contrast-date-utils'
import { formatUSD, formatPercent, formatNumber } from '@/lib/format-utils'
import { getWeek } from '@/lib/date-utils'

// 生成对比信息的显示文本（根据显示选项控制显示内容）
const formatContrastInfo = (
  contrastValue,
  diff,
  diffPercent,
  formatter,
  showContrastValue,
  showDiff,
  showDiffPercent,
) => {
  if (contrastValue == null) {
    return '-'
  }

  const parts = []

  // 原始值（对比值）
  if (showContrastValue) {
    const formattedContrast = formatter(contrastValue)
    parts.push(formattedContrast)
  }

  // 差异值
  if (showDiff) {
    const diffValue = diff
    if (diffValue != null && !isNaN(diffValue)) {
      const diffFormatted = formatter(Math.abs(diffValue))
      const arrow = diffValue >= 0 ? '↑' : '↓'
      const colorClass = diffValue >= 0 ? 'text-green-600' : 'text-red-600'
      parts.push(
        `<span class="${colorClass}">${arrow}${diffFormatted}</span>`,
      )
    }
  }

  // 差异百分比
  if (showDiffPercent) {
    if (diffPercent != null && !isNaN(diffPercent)) {
      const percentFormatted =
        diffPercent >= 0
          ? `+${diffPercent.toFixed(1)}%`
          : `${diffPercent.toFixed(1)}%`
      const colorClass = diffPercent >= 0 ? 'text-green-600' : 'text-red-600'
      parts.push(`<span class="${colorClass}">${percentFormatted}</span>`)
    }
  }

  if (parts.length === 0) {
    return '-'
  }

  return parts.join(' ')
}

// 带对比信息的单元格组件
const Td2WithContrast = ({
  children,
  contrastValue,
  diff,
  diffPercent,
  line1Css,
  boldLine1 = true,
  className,
  formatter = (v) => String(v),
  originalLine2 = null,
}) => {
  // 使用 hook 订阅状态变化，确保组件能够响应状态更新
  const contrastEnabled = useContrastModeStore((state) => state.enabled)
  const showContrastValue = useContrastModeStore(
    (state) => state.showContrastValue,
  )
  const showDiff = useContrastModeStore((state) => state.showDiff)
  const showDiffPercent = useContrastModeStore(
    (state) => state.showDiffPercent,
  )

  const line2 =
    contrastEnabled && contrastValue != null
      ? formatContrastInfo(
          contrastValue,
          diff,
          diffPercent,
          formatter,
          showContrastValue,
          showDiff,
          showDiffPercent,
        )
      : originalLine2 || '-'

  return (
    <div className={cn('flex flex-col text-right', className)}>
      <div className={cn('text-nowrap', boldLine1 && 'font-bold', line1Css)}>
        {children}
      </div>
      <div
        className="text-xs text-muted-foreground"
        dangerouslySetInnerHTML={{ __html: line2 }}
      />
    </div>
  )
}

const formatPercentValue = (value) => {
  const num = parseFloat(value)
  return isNaN(num) ? '0.00%' : `${(num * 100).toFixed(2)}%`
}

const formatMetric = (metric, value) => {
  const num = parseFloat(value)
  if (isNaN(num)) return value

  if (['estimated_earnings'].includes(metric)) {
    return `$${num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }
  if (['observed_ecpm'].includes(metric)) {
    return `$${num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }
  if (['match_rate', 'show_rate', 'ctr'].includes(metric)) {
    return formatPercent(num)
  }
  if (
    ['ad_requests', 'matched_requests', 'impressions', 'clicks'].includes(
      metric,
    )
  ) {
    return num.toLocaleString()
  }
  return num.toLocaleString()
}

function AdmobReportPage() {
  const { filters } = useAdmobStore()
  const { startDate, endDate } = useAdmobDatePickerStore()
  const [enlargedChart, setEnlargedChart] = useState(null)
  const [isManualCheckOpen, setIsManualCheckOpen] = useState(false)

  // 获取对比模式状态
  const contrastEnabled = useContrastModeStore((state) => state.enabled)
  const contrastType = useContrastModeStore((state) => state.type)
  const customYear = useContrastModeStore((state) => state.customYear)
  const customMonth = useContrastModeStore((state) => state.customMonth)

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setEnlargedChart(null)
      }
    }

    if (enlargedChart) {
      document.body.classList.add('overflow-hidden')
      window.addEventListener('keydown', handleKeyDown)
    } else {
      document.body.classList.remove('overflow-hidden')
    }

    return () => {
      document.body.classList.remove('overflow-hidden')
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [enlargedChart])

  // Helper to convert YYYYMMDD string to a valid Date object
  const parseDate = (dateString) => {
    if (!dateString || dateString.length !== 8) return null
    const year = dateString.substring(0, 4)
    const month = dateString.substring(4, 6)
    const day = dateString.substring(6, 8)
    return new Date(`${year}-${month}-${day}`)
  }

  // 日期格式转换工具函数
  const formatDateForContrast = (dateStr) => {
    // 将 "2024-01-01" 转换为 20240101
    return parseInt(dateStr.replace(/-/g, ''), 10)
  }

  const parseContrastDate = (dateNum) => {
    // 将 20240101 转换为 "2024-01-01"
    const dateStr = String(dateNum)
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`
  }

  const buildDateList = (start, end) => {
    if (!start || !end) return []
    const startObj = dayjs(String(start), 'YYYYMMDD')
    const endObj = dayjs(String(end), 'YYYYMMDD')
    if (!startObj.isValid() || !endObj.isValid()) return []
    const result = []
    let cursor = startObj.startOf('day')
    const last = endObj.startOf('day')
    while (!cursor.isAfter(last)) {
      result.push(cursor.format('YYYY-MM-DD'))
      cursor = cursor.add(1, 'day')
    }
    return result
  }

  // 创建多维度匹配键的工具函数
  const createMatchKey = (row, dimensions) => {
    return dimensions
      .filter((d) => d !== 'date') // date 单独处理
      .map((d) => row[d] || 'N/A')
      .join('|')
  }

  // 计算对比日期范围
  const contrastDateRange = useMemo(() => {
    if (!contrastEnabled || !contrastType) {
      return { startDate: null, endDate: null }
    }
    return getContrastDateRange(
      parseInt(startDate, 10),
      parseInt(endDate, 10),
      contrastType,
      customYear,
      customMonth
    )
  }, [contrastEnabled, contrastType, startDate, endDate, customYear, customMonth])

  const tableQueryParams = useMemo(() => {
    const apiFilters = {}
    if (filters.pid !== 'total') {
      apiFilters.pid = [filters.pid]
    }
    if (filters.package_name && filters.package_name !== 'total' && hasProductPackages(filters.pid)) {
      apiFilters.package_name = [filters.package_name]
    }
    const multiCountries = (filters.countries || []).filter(
      (code) => code && code !== 'total',
    )
    const fallbackCountry =
      filters.country && filters.country !== 'total' ? [filters.country] : []
    const selectedCountries =
      multiCountries.length > 0 ? multiCountries : fallbackCountry

    if (selectedCountries.length > 0) {
      apiFilters.country_code = selectedCountries
    }
    if (filters.ad_unit.length > 0) {
      apiFilters.ad_unit_id = filters.ad_unit
    }
    if (filters.app_version.length > 0) {
      apiFilters.app_version_name = filters.app_version
    }
    if (filters.format.length > 0) {
      apiFilters.format = filters.format
    }

    const params = {
      startDate: format(parseDate(startDate), 'yyyyMMdd'),
      endDate: format(parseDate(endDate), 'yyyyMMdd'),
      pid: filters.pid,
      country: filters.country,
      dimensions: filters.dimensions.join(','),
      metrics: filters.metrics.join(','),
    }

    if (selectedCountries.length > 0) {
      params.countries = selectedCountries.join(',')
    }

    if (Object.keys(apiFilters).length > 0) {
      params.filters = JSON.stringify(apiFilters)
    }

    return params
  }, [startDate, endDate, filters])

  // 创建日期列（需要在组件内部以访问状态）
  const dateColumn = React.useMemo(() => ({
    accessorKey: 'date',
    header: 'Date',
    cell: ({ row }) => {
      const data = row.original
      const currentDateStr = data.date
      const currentDateNum = formatDateForContrast(currentDateStr)
      const [, weekDay] = getWeek(currentDateNum)
      const formattedDate = format(new Date(currentDateStr), 'yyyy/MM/dd')
      
      // 如果对比模式启用，显示对比日期（即使没有对比数据也显示）
      if (contrastEnabled && contrastType) {
        const contrastDateNum = getContrastDate(
          currentDateNum,
          contrastType,
          customYear,
          customMonth,
        )
        if (contrastDateNum) {
          const contrastDateStr = parseContrastDate(contrastDateNum)
          const [, contrastWeekDay] = getWeek(contrastDateNum)
          const formattedContrastDate = format(
            new Date(contrastDateStr),
            'yyyy/MM/dd',
          )
          return (
            <div className="flex flex-col text-right">
              <div className="font-bold">{formattedDate}</div>
              <div className="font-bold">周{weekDay}</div>
              <div className="text-xs text-muted-foreground">{formattedContrastDate}</div>
              <div className="text-xs text-muted-foreground">周{contrastWeekDay}</div>
            </div>
          )
        }
      }
      return (
        <div className="flex flex-col text-right">
          <div className="font-bold">{formattedDate}</div>
          <div className="font-bold">周{weekDay}</div>
        </div>
      )
    },
  }), [contrastEnabled, contrastType, customYear, customMonth])

  const staticColumns = [
    dateColumn,
    {
      accessorKey: 'estimated_earnings',
      header: 'Est. Earnings',
      cell: ({ row }) => {
        const data = row.original
        const isContrastOnly = data.isContrastOnly
        const currentValue = isContrastOnly
          ? null
          : parseFloat(data.estimated_earnings) || 0
        const formatted = isContrastOnly ? '-' : formatUSD(currentValue)

        const contrastEnabled = useContrastModeStore((state) => state.enabled)
        if (contrastEnabled && data.contrast) {
          return (
            <Td2WithContrast
              contrastValue={data.contrast.estimated_earnings}
              diff={data.diff?.estimated_earnings}
              diffPercent={data.diffPercent?.estimated_earnings}
              formatter={formatUSD}
            >
              {formatted}
            </Td2WithContrast>
          )
        }
        return <div className="text-right font-bold">{formatted}</div>
      },
    },
    {
      accessorKey: 'observed_ecpm',
      header: 'eCPM',
      cell: ({ row }) => {
        const data = row.original
        const isContrastOnly = data.isContrastOnly
        const currentValue = isContrastOnly
          ? null
          : parseFloat(data.observed_ecpm) || 0
        const formatted = isContrastOnly ? '-' : formatUSD(currentValue)

        const contrastEnabled = useContrastModeStore((state) => state.enabled)
        if (contrastEnabled && data.contrast) {
          const contrastValue =
            data.contrast.impressions > 0
              ? (data.contrast.estimated_earnings /
                  data.contrast.impressions) *
                1000
              : 0
          return (
            <Td2WithContrast
              contrastValue={contrastValue}
              diff={data.diff?.observed_ecpm}
              diffPercent={data.diffPercent?.observed_ecpm}
              formatter={formatUSD}
            >
              {formatted}
            </Td2WithContrast>
          )
        }
        return <div className="text-right font-bold">{formatted}</div>
      },
    },
    {
      accessorKey: 'ad_requests',
      header: 'Ad Requests',
      cell: ({ row }) => {
        const data = row.original
        const isContrastOnly = data.isContrastOnly
        const currentValue = isContrastOnly ? null : parseFloat(data.ad_requests) || 0
        const formatted = isContrastOnly ? '-' : formatNumber(currentValue)

        const contrastEnabled = useContrastModeStore((state) => state.enabled)
        if (contrastEnabled && data.contrast) {
          return (
            <Td2WithContrast
              contrastValue={data.contrast.ad_requests}
              diff={data.diff?.ad_requests}
              diffPercent={data.diffPercent?.ad_requests}
              formatter={formatNumber}
            >
              {formatted}
            </Td2WithContrast>
          )
        }
        return <div className="text-right font-bold">{formatted}</div>
      },
    },
    {
      accessorKey: 'match_rate',
      header: 'Match Rate',
      cell: ({ row }) => {
        const data = row.original
        const isContrastOnly = data.isContrastOnly
        const currentValue = isContrastOnly ? null : parseFloat(data.match_rate) || 0
        const formatted = isContrastOnly ? '-' : formatPercentValue(currentValue)

        const contrastEnabled = useContrastModeStore((state) => state.enabled)
        if (contrastEnabled && data.contrast && data.contrast.ad_requests) {
          const contrastValue =
            data.contrast.ad_requests > 0
              ? (data.contrast.matched_requests || 0) /
                data.contrast.ad_requests
              : 0
          return (
            <Td2WithContrast
              contrastValue={contrastValue}
              diff={data.diff?.match_rate}
              diffPercent={data.diffPercent?.match_rate}
              formatter={formatPercentValue}
            >
              {formatted}
            </Td2WithContrast>
          )
        }
        return <div className="text-right font-bold">{formatted}</div>
      },
    },
    {
      accessorKey: 'matched_requests',
      header: 'Matched Requests',
      cell: ({ row }) => {
        const data = row.original
        const isContrastOnly = data.isContrastOnly
        const currentValue = isContrastOnly
          ? null
          : parseFloat(data.matched_requests) || 0
        const formatted = isContrastOnly ? '-' : formatNumber(currentValue)

        const contrastEnabled = useContrastModeStore((state) => state.enabled)
        if (contrastEnabled && data.contrast) {
          return (
            <Td2WithContrast
              contrastValue={data.contrast.matched_requests}
              diff={data.diff?.matched_requests}
              diffPercent={data.diffPercent?.matched_requests}
              formatter={formatNumber}
            >
              {formatted}
            </Td2WithContrast>
          )
        }
        return <div className="text-right font-bold">{formatted}</div>
      },
    },
    {
      accessorKey: 'show_rate',
      header: 'Show Rate',
      cell: ({ row }) => {
        const data = row.original
        const isContrastOnly = data.isContrastOnly
        const currentValue = isContrastOnly ? null : parseFloat(data.show_rate) || 0
        const formatted = isContrastOnly ? '-' : formatPercentValue(currentValue)

        const contrastEnabled = useContrastModeStore((state) => state.enabled)
        if (contrastEnabled && data.contrast && data.contrast.matched_requests) {
          const contrastValue =
            data.contrast.matched_requests > 0
              ? (data.contrast.impressions || 0) /
                data.contrast.matched_requests
              : 0
          return (
            <Td2WithContrast
              contrastValue={contrastValue}
              diff={data.diff?.show_rate}
              diffPercent={data.diffPercent?.show_rate}
              formatter={formatPercentValue}
            >
              {formatted}
            </Td2WithContrast>
          )
        }
        return <div className="text-right font-bold">{formatted}</div>
      },
    },
    {
      accessorKey: 'impressions',
      header: 'Impressions',
      cell: ({ row }) => {
        const data = row.original
        const isContrastOnly = data.isContrastOnly
        const currentValue = isContrastOnly
          ? null
          : parseFloat(data.impressions) || 0
        const formatted = isContrastOnly ? '-' : formatNumber(currentValue)

        const contrastEnabled = useContrastModeStore((state) => state.enabled)
        if (contrastEnabled && data.contrast) {
          return (
            <Td2WithContrast
              contrastValue={data.contrast.impressions}
              diff={data.diff?.impressions}
              diffPercent={data.diffPercent?.impressions}
              formatter={formatNumber}
            >
              {formatted}
            </Td2WithContrast>
          )
        }
        return <div className="text-right font-bold">{formatted}</div>
      },
    },
    {
      accessorKey: 'impression_ctr',
      header: 'Impression CTR',
      cell: ({ row }) => {
        const data = row.original
        const isContrastOnly = data.isContrastOnly
        const currentValue = isContrastOnly
          ? null
          : parseFloat(data.impression_ctr) || 0
        const formatted = isContrastOnly ? '-' : formatPercentValue(currentValue)

        const contrastEnabled = useContrastModeStore((state) => state.enabled)
        if (contrastEnabled && data.contrast && data.contrast.impressions) {
          const contrastValue =
            data.contrast.impressions > 0
              ? (data.contrast.clicks || 0) / data.contrast.impressions
              : 0
          return (
            <Td2WithContrast
              contrastValue={contrastValue}
              diff={data.diff?.impression_ctr}
              diffPercent={data.diffPercent?.impression_ctr}
              formatter={formatPercentValue}
            >
              {formatted}
            </Td2WithContrast>
          )
        }
        return <div className="text-right font-bold">{formatted}</div>
      },
    },
    {
      accessorKey: 'clicks',
      header: 'Clicks',
      cell: ({ row }) => {
        const data = row.original
        const isContrastOnly = data.isContrastOnly
        const currentValue = isContrastOnly ? null : parseFloat(data.clicks) || 0
        const formatted = isContrastOnly ? '-' : formatNumber(currentValue)

        const contrastEnabled = useContrastModeStore((state) => state.enabled)
        if (contrastEnabled && data.contrast) {
          return (
            <Td2WithContrast
              contrastValue={data.contrast.clicks}
              diff={data.diff?.clicks}
              diffPercent={data.diffPercent?.clicks}
              formatter={formatNumber}
            >
              {formatted}
            </Td2WithContrast>
          )
        }
        return <div className="text-right font-bold">{formatted}</div>
      },
    },
  ]

  const metricLabels = staticColumns.reduce((acc, col) => {
    if (col.accessorKey !== 'date') {
      acc[col.accessorKey] = col.header
    }
    return acc
  }, {})

  const columns = React.useMemo(() => {
    const dimensionColumns = filters.dimensions
      .filter((d) => d !== 'date')
      .map((dim) => ({
        accessorKey: dim,
        header: dim.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        cell: ({ row }) => row.original[dim] || 'N/A',
      }))

    if (filters.app_version && filters.app_version.length > 0) {
      dimensionColumns.unshift({
        accessorKey: 'app_version',
        header: 'App Version',
        cell: ({ row }) => row.original.app_version || 'N/A',
      })
    }

    return [...dimensionColumns, ...staticColumns]
  }, [filters.dimensions, filters.app_version])

  // 构建对比查询参数
  const contrastQueryParams = useMemo(() => {
    if (
      !contrastEnabled ||
      !contrastDateRange.startDate ||
      !contrastDateRange.endDate
    ) {
      return null
    }
    return {
      ...tableQueryParams,
      startDate: String(contrastDateRange.startDate),
      endDate: String(contrastDateRange.endDate),
    }
  }, [contrastEnabled, contrastDateRange, tableQueryParams])

  const {
    data: tableData,
    error: tableError,
    isLoading: isTableLoading,
  } = useSWR(['/admob/report', tableQueryParams], fetcher)

  // 查询对比数据
  const {
    data: contrastTableData,
    isLoading: isContrastLoading,
  } = useSWR(
    contrastQueryParams ? ['/admob/report', contrastQueryParams] : null,
    fetcher
  )

  const { trigger, isMutating } = useSWRMutation(
    ['/admob/report/download', tableQueryParams],
    blobFetcher,
  )

  const { trigger: triggerPam, isMutating: isPamMutating } = useSWRMutation(
    ['/admob/report/pam', tableQueryParams],
    blobFetcher,
  )

  const { trigger: triggerRcAudit, isMutating: isRcAuditMutating } =
    useSWRMutation('/admob/ad-pos-conf-audit/run', postFetcher)

  const { data: rcAuditStatus, mutate: mutateRcAuditStatus } = useSWR(
    ['/admob/ad-pos-conf-audit/status'],
    fetcher,
    {
      refreshInterval: (latestData) =>
        latestData?.running ? 5000 : 0,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    },
  )

  const isRcAuditRunning = Boolean(
    isRcAuditMutating || rcAuditStatus?.running,
  )

  const handleCheckRcAdmob = async () => {
    if (isRcAuditRunning) return
    try {
      await mutateRcAuditStatus(
        { running: true, startedAt: new Date().toISOString() },
        { revalidate: false },
      )
      const result = await triggerRcAudit({})
      await mutateRcAuditStatus()
      const coverage = result?.positionCoverage
      if (result?.feishuSent === 'alert') {
        toast.warning(
          `Found ${result.totalInvalid} invalid unitId(s), ${coverage?.missing ?? 0} missing required position(s), and ${coverage?.errors ?? 0} coverage error(s). Feishu alert sent.`,
        )
      } else if (result?.feishuSent === 'pass') {
        toast.success(
          `All ${result.productsChecked} product(s) passed (${result.totalUnitIds} unitIds and ${coverage?.required ?? 0} required position(s) checked). Feishu report sent.`,
        )
      } else {
        toast.success(
          `Audit completed: ${result?.productsChecked ?? 0} product(s), ${result?.totalInvalid ?? 0} invalid unitId(s), ${coverage?.missing ?? 0} missing required position(s), ${coverage?.errors ?? 0} coverage error(s).`,
        )
      }
    } catch (error) {
      await mutateRcAuditStatus()
      console.error('RC AdMob audit failed', error)
      const message = error?.message || 'RC AdMob audit failed'
      if (message.includes('already running')) {
        toast.info('RC AdMob audit is already in progress')
      } else {
        toast.error(message)
      }
    }
  }

  const handleExport = async (isMutating) => {
    if (isMutating) return
    const { blob, fileName } = await trigger()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName || 'admob-report.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleExportPam = async (isPamMutating) => {
    if (isPamMutating) return
    try {
      const { blob, fileName } = await triggerPam()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName || 'admob-report-pam.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Failed to export PAM report', error)
      alert(error?.message || 'Failed to export PAM report')
    }
  }

  const chartId = JSON.stringify(tableQueryParams)

  const processedTableData = useMemo(() => {
    if (!tableData) return []
    
    const dateList = buildDateList(startDate, endDate)
    const currentDataMap = {}
    // 将对比数据按日期和所有维度索引
    const contrastDataMap = {}
    if (contrastTableData) {
      contrastTableData.forEach((row) => {
        const date = row.date
        const matchKey = createMatchKey(row, filters.dimensions)
        if (!contrastDataMap[date]) {
          contrastDataMap[date] = {}
        }
        contrastDataMap[date][matchKey] = row
      })
    }

    tableData.forEach((row) => {
      const date = row.date
      const matchKey = createMatchKey(row, filters.dimensions)
      if (!currentDataMap[date]) {
        currentDataMap[date] = {}
      }
      currentDataMap[date][matchKey] = row
    })

    const sortedData = [...tableData].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    )
    const baseProcessed = sortedData.map((row) => {
      const impressions = parseFloat(row.impressions) || 0
      const clicks = parseFloat(row.clicks) || 0
      const ad_requests = parseFloat(row.ad_requests) || 0
      const matched_requests = parseFloat(row.matched_requests) || 0

      const impression_ctr = impressions > 0 ? clicks / impressions : 0
      const match_rate = ad_requests > 0 ? matched_requests / ad_requests : 0
      const show_rate =
        matched_requests > 0 ? impressions / matched_requests : 0
      const observed_ecpm =
        impressions > 0 ? (row.estimated_earnings / impressions) * 1000 : 0

      const processed = {
        ...row,
        impression_ctr,
        match_rate,
        show_rate,
        observed_ecpm,
      }

      // 处理对比数据
      if (contrastEnabled && contrastType) {
        // 计算当前日期对应的对比日期
        const currentDateStr = row.date // "2024-01-01"
        const currentDateNum = formatDateForContrast(currentDateStr)
        const contrastDateNum = getContrastDate(
          currentDateNum,
          contrastType,
          customYear,
          customMonth
        )

        if (contrastDateNum) {
          const contrastDateStr = parseContrastDate(contrastDateNum)
          const currentMatchKey = createMatchKey(row, filters.dimensions)
          const contrastRow =
            contrastDataMap[contrastDateStr]?.[currentMatchKey] || null

          if (contrastRow) {
            // 附加对比数据
            processed.contrast = contrastRow

            // 计算差异值和差异百分比
            processed.diff = {}
            processed.diffPercent = {}

            // 为每个指标计算差异
            const metrics = [
              'estimated_earnings',
              'observed_ecpm',
              'ad_requests',
              'matched_requests',
              'impressions',
              'clicks',
            ]
            metrics.forEach((metric) => {
              const current = parseFloat(row[metric]) || 0
              const contrast = parseFloat(contrastRow[metric]) || 0
              processed.diff[metric] = current - contrast
              processed.diffPercent[metric] =
                contrast !== 0 ? ((current - contrast) / contrast) * 100 : null
            })

            // 计算比率指标的差异
            if (row.ad_requests && contrastRow.ad_requests) {
              const currentMatchRate =
                (row.matched_requests || 0) / row.ad_requests || 0
              const contrastMatchRate =
                (contrastRow.matched_requests || 0) / contrastRow.ad_requests ||
                0
              processed.diff.match_rate = currentMatchRate - contrastMatchRate
              processed.diffPercent.match_rate =
                contrastMatchRate !== 0
                  ? ((currentMatchRate - contrastMatchRate) / contrastMatchRate) *
                    100
                  : null
            }

            // 计算 show_rate
            if (row.matched_requests && contrastRow.matched_requests) {
              const currentShowRate =
                (row.impressions || 0) / row.matched_requests || 0
              const contrastShowRate =
                (contrastRow.impressions || 0) / contrastRow.matched_requests ||
                0
              processed.diff.show_rate = currentShowRate - contrastShowRate
              processed.diffPercent.show_rate =
                contrastShowRate !== 0
                  ? ((currentShowRate - contrastShowRate) / contrastShowRate) *
                    100
                  : null
            }

            // 计算 impression_ctr
            if (row.impressions && contrastRow.impressions) {
              const currentCtr = (row.clicks || 0) / row.impressions || 0
              const contrastCtr =
                (contrastRow.clicks || 0) / contrastRow.impressions || 0
              processed.diff.impression_ctr = currentCtr - contrastCtr
              processed.diffPercent.impression_ctr =
                contrastCtr !== 0
                  ? ((currentCtr - contrastCtr) / contrastCtr) * 100
                  : null
            }

            // 计算 observed_ecpm 的差异
            const currentObservedEcpm = processed.observed_ecpm
            const contrastObservedEcpm =
              contrastRow.impressions > 0
                ? (contrastRow.estimated_earnings / contrastRow.impressions) *
                  1000
                : 0
            processed.diff.observed_ecpm =
              currentObservedEcpm - contrastObservedEcpm
            processed.diffPercent.observed_ecpm =
              contrastObservedEcpm !== 0
                ? ((currentObservedEcpm - contrastObservedEcpm) /
                    contrastObservedEcpm) *
                  100
                : null
          }
        }
      }

      return processed
    })

    if (!contrastEnabled || !contrastType || !contrastTableData) {
      return baseProcessed
    }

    // 补齐：当前日期没有数据但对比日期有数据的行
    const completedRows = [...baseProcessed]
    const metrics = [
      'estimated_earnings',
      'observed_ecpm',
      'ad_requests',
      'matched_requests',
      'impressions',
      'clicks',
      'match_rate',
      'show_rate',
      'impression_ctr',
    ]

    dateList.forEach((dateStr) => {
      const currentDateNum = formatDateForContrast(dateStr)
      const contrastDateNum = getContrastDate(
        currentDateNum,
        contrastType,
        customYear,
        customMonth,
      )
      if (!contrastDateNum) return
      const contrastDateStr = parseContrastDate(contrastDateNum)
      const contrastByKey = contrastDataMap[contrastDateStr]
      if (!contrastByKey) return

      Object.values(contrastByKey).forEach((contrastRow) => {
        const matchKey = createMatchKey(contrastRow, filters.dimensions)
        if (currentDataMap[dateStr]?.[matchKey]) return

        const placeholder = {
          ...contrastRow,
          date: dateStr,
          isContrastOnly: true,
          contrast: contrastRow,
          diff: null,
          diffPercent: null,
        }
        metrics.forEach((metric) => {
          placeholder[metric] = null
        })
        completedRows.push(placeholder)
      })
    })

    return completedRows.sort((a, b) => {
      const dateDiff = new Date(b.date) - new Date(a.date)
      if (dateDiff !== 0) return dateDiff
      const keyA = createMatchKey(a, filters.dimensions)
      const keyB = createMatchKey(b, filters.dimensions)
      return keyA.localeCompare(keyB)
    })
  }, [
    tableData,
    contrastTableData,
    contrastEnabled,
    contrastType,
    customYear,
    customMonth,
    filters.dimensions,
    startDate,
    endDate,
  ])

  const chartEnabled = filters.dimensions.includes('date')

  const renderContent = useMemo(() => {
    if (isTableLoading) {
      return <Skeleton className="w-full h-96" />
    }
    if (tableError) {
      return <div className="text-red-500">Failed to load data.</div>
    }
    if (!tableData || tableData.length === 0) {
      return <div>No data available.</div>
    }

    const charts = [
      {
        id: 'earnings',
        metric: 'estimated_earnings',
        title: 'Est. Earnings',
      },
      { id: 'ecpm', metric: 'observed_ecpm', title: 'eCPM' },
      { id: 'requests', metric: 'ad_requests', title: 'Ad Requests' },
      {
        id: 'matched-requests',
        metric: 'matched_requests',
        title: 'Matched Requests',
      },
      { id: 'impressions', metric: 'impressions', title: 'Impressions' },
      { id: 'clicks', metric: 'clicks', title: 'Clicks' },
    ]

    return (
      <>
        {chartEnabled && (
          <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4 flex-1">
            {charts.map((chart) => (
              <div
                key={chart.id}
                className={cn('relative group', {
                  'fixed inset-0 bg-white z-50 p-4 flex flex-col':
                    enlargedChart === chart.id,
                  'min-h-[200px] flex flex-col flex-1': !enlargedChart,
                  invisible: enlargedChart && enlargedChart !== chart.id,
                })}
              >
                <AdmobChart
                  chartId={`${chart.id}-${chartId}`}
                  baseQueryParams={tableQueryParams}
                  contrastQueryParams={contrastQueryParams}
                  baseMetric={chart.metric}
                  title={chart.title}
                  metricLabels={metricLabels}
                  isEnlarged={enlargedChart === chart.id}
                  onToggleEnlarge={() =>
                    setEnlargedChart(
                      enlargedChart === chart.id ? null : chart.id,
                    )
                  }
                />
              </div>
            ))}
          </div>
        )}
        <AdmobDataTable
          data={processedTableData}
          columns={columns}
          isLoading={isTableLoading}
        />
      </>
    )
  }, [
    tableData,
    tableError,
    isTableLoading,
    processedTableData,
    columns,
    chartId,
    metricLabels,
    chartEnabled,
    tableQueryParams,
    enlargedChart,
  ])

  return (
    <div className="container mx-auto p-4 flex flex-col h-full">
      <AdmobReportFilters
        onExport={() => handleExport(isMutating)}
        onExportPam={() => handleExportPam(isPamMutating)}
        onCheckRcAdmob={handleCheckRcAdmob}
        onOpenManualCheck={() => setIsManualCheckOpen(true)}
        isMutating={isMutating}
        isPamMutating={isPamMutating}
        isRcAuditMutating={isRcAuditRunning}
      />
      <RcAdmobManualCheckDialog
        open={isManualCheckOpen}
        onOpenChange={setIsManualCheckOpen}
      />
      <div className="mt-4 flex-1">{renderContent}</div>
    </div>
  )
}

export default AdmobReportPage
